import { resolveMediaUrl } from '@/lib/media';
import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, Calendar, MapPin, Clock, Users, Trash2, Edit2, Eye, EyeOff, Upload, Video, DollarSign, Globe, Monitor, User, ChevronDown, Loader2, X, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import FileDropzone, { IMAGE_UPLOAD, VIDEO_UPLOAD } from './FileDropzone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
  { id: 'in_person', label: 'In-Person', icon: MapPin },
  { id: 'online', label: 'Online', icon: Monitor },
  { id: 'hybrid', label: 'Hybrid', icon: Globe },
];

const getEventStatus = (evt) => {
  const now = new Date();
  const start = new Date(`${evt.date_start}T${evt.time_start || '00:00'}`);
  const endDate = evt.date_end || evt.date_start;
  const end = new Date(`${endDate}T${evt.time_end || '23:59'}`);
  if (now < start) return 'upcoming';
  if (now >= start && now <= end) return 'live';
  return 'past';
};

const STATUS_STYLES = {
  upcoming: { bg: 'bg-blue-500/15', text: 'text-blue-400', label: 'Upcoming' },
  live: { bg: 'bg-green-500/15', text: 'text-green-400', label: 'Live Now' },
  past: { bg: 'bg-[#27272A]', text: 'text-[#71717A]', label: 'Past' },
};

const EventsManager = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rsvpView, setRsvpView] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const fileRef = useRef(null);
  const videoFileRef = useRef(null);

  const emptyForm = {
    title: '', description: '', poster_url: '', video_url: '',
    date_start: '', date_end: '', time_start: '', time_end: '',
    location_name: '', location_address: '', organized_by: '',
    event_type: 'in_person', meeting_link: '', max_capacity: '',
    price: 0, is_active: true,
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events`);
      setEvents(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.date_start) { toast.error('Start date is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, max_capacity: form.max_capacity ? parseInt(form.max_capacity) : null, price: parseFloat(form.price) || 0 };
      if (editingId) {
        const res = await axios.put(`${API}/events/${editingId}`, payload);
        setEvents(events.map(e => e.id === editingId ? res.data : e));
        toast.success('Event updated');
      } else {
        const res = await axios.post(`${API}/events`, payload);
        setEvents([...events, res.data]);
        toast.success('Event created!');
      }
      resetForm();
    } catch (e) { toast.error('Failed to save event'); }
    finally { setSaving(false); }
  };

  const handleEdit = (evt) => {
    setForm({ ...emptyForm, ...evt, max_capacity: evt.max_capacity || '' });
    setEditingId(evt.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/events/${id}`);
      setEvents(events.filter(e => e.id !== id));
      toast.success('Event deleted');
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleToggle = async (evt) => {
    try {
      const res = await axios.put(`${API}/events/${evt.id}`, { is_active: !evt.is_active });
      setEvents(events.map(e => e.id === evt.id ? res.data : e));
    } catch (e) { toast.error('Failed to toggle'); }
  };

  const handleUploadPoster = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, poster_url: res.data.url }));
      toast.success('Poster uploaded');
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleUploadVideo = async (file) => {
    if (!file) return;
    setUploadingVideo(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload-video`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, video_url: res.data.url }));
      toast.success('Video uploaded');
    } catch (e) { toast.error('Video upload failed'); }
    finally { setUploadingVideo(false); }
  };

  const viewRSVPs = async (evt) => {
    setRsvpView(evt);
    try {
      const res = await axios.get(`${API}/events/${evt.id}/rsvps`);
      setRsvps(res.data);
    } catch (e) { toast.error('Failed to load RSVPs'); }
  };

  const resetForm = () => { setForm(emptyForm); setEditingId(null); setShowForm(false); };
  const updateForm = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  return (
    <div className="max-w-4xl" data-testid="events-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">Events</h1>
          <p className="text-[#A1A1AA] mt-1 text-sm">Create events and invite people via your link</p>
        </div>
        <Button data-testid="add-event-btn" onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-violet-600 hover:bg-violet-700 rounded-xl">
          <Plus className="w-4 h-4 mr-1" /> Add Event
        </Button>
      </div>

      {/* RSVP Viewer Modal */}
      {rsvpView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setRsvpView(null)}>
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            onClick={e => e.stopPropagation()}
            className="bg-[#121212] border border-[#27272A] rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">RSVPs - {rsvpView.title}</h3>
              <button onClick={() => setRsvpView(null)}><X className="w-5 h-5 text-[#71717A]" /></button>
            </div>
            {rsvps.length > 0 ? (
              <div className="space-y-2">
                {rsvps.map(r => (
                  <div key={r.id} className="p-3 rounded-xl bg-white/5 border border-[#27272A]">
                    <p className="text-white text-sm font-medium">{r.name}</p>
                    <p className="text-[#A1A1AA] text-xs">{r.email} {r.phone && `| ${r.phone}`}</p>
                    <p className="text-[#525252] text-[10px] mt-1">{new Date(r.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#71717A] text-sm text-center py-8">No RSVPs yet</p>
            )}
          </motion.div>
        </div>
      )}

      {/* Event Form */}
      {showForm && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-[#121212] rounded-2xl border border-[#27272A] p-5 mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-white font-semibold">{editingId ? 'Edit Event' : 'New Event'}</h2>
            <button onClick={resetForm}><X className="w-5 h-5 text-[#71717A]" /></button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-[#A1A1AA] text-xs">Title *</Label>
              <Input data-testid="event-title" value={form.title} onChange={e => updateForm('title', e.target.value)}
                className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="Event title" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[#A1A1AA] text-xs">Description</Label>
              <Textarea data-testid="event-desc" value={form.description} onChange={e => updateForm('description', e.target.value)}
                className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white min-h-[80px]" placeholder="Tell people what this event is about..." />
            </div>

            {/* Poster */}
            <div className="sm:col-span-2">
              <Label className="text-[#A1A1AA] text-xs">Poster Image</Label>
              <FileDropzone
                {...IMAGE_UPLOAD}
                compact
                className="mt-1"
                testId="event-poster-dropzone"
                label={form.poster_url ? 'Replace poster or drag a new image' : 'Choose poster or drag and drop'}
                disabled={uploading}
                onUploaded={(result) => setForm(prev => ({ ...prev, poster_url: result.url }))}
                successMessage="Poster uploaded"
              />
              <div className="mt-1 flex items-center gap-3">
                {form.poster_url && (
                  <img src={resolveMediaUrl(form.poster_url)} alt="" className="w-20 h-14 rounded-lg object-cover border border-[#27272A]" />
                )}
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="border-[#27272A] text-[#A1A1AA] text-xs rounded-lg">
                  {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                  {form.poster_url ? 'Change' : 'Upload'}
                </Button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => handleUploadPoster(e.target.files?.[0])} />
              </div>
            </div>

            <div className="sm:col-span-2">
              <Label className="text-[#A1A1AA] text-xs">Video</Label>
              <FileDropzone
                {...VIDEO_UPLOAD}
                compact
                className="mt-1"
                testId="event-video-dropzone"
                label={form.video_url ? 'Replace video or drag a new file' : 'Choose video or drag and drop'}
                disabled={uploadingVideo}
                onUploaded={(result) => setForm(prev => ({ ...prev, video_url: result.url }))}
                successMessage="Video uploaded"
              />
              <div className="mt-1 flex items-center gap-3">
                {form.video_url && (
                  <div className="w-20 h-14 rounded-lg border border-[#27272A] bg-[#1E1E1E] flex items-center justify-center overflow-hidden">
                    {form.video_url.includes('youtube') || form.video_url.includes('youtu.be') || form.video_url.includes('vimeo') ? (
                      <Video className="w-6 h-6 text-violet-400" />
                    ) : (
                      <video src={resolveMediaUrl(form.video_url)} className="w-full h-full object-cover" muted />
                    )}
                  </div>
                )}
                <Button variant="outline" size="sm" onClick={() => videoFileRef.current?.click()} disabled={uploadingVideo}
                  className="border-[#27272A] text-[#A1A1AA] text-xs rounded-lg">
                  {uploadingVideo ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                  Upload Video
                </Button>
                <span className="text-[#525252] text-xs">or</span>
                <input ref={videoFileRef} type="file" accept="video/*" className="hidden" onChange={e => handleUploadVideo(e.target.files?.[0])} />
              </div>
              <Input data-testid="event-video-url" value={form.video_url} onChange={e => updateForm('video_url', e.target.value)}
                className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white text-sm"
                placeholder="Or paste video URL (YouTube, Vimeo, etc.)" />
            </div>

            {/* Date/Time */}
            <div>
              <Label className="text-[#A1A1AA] text-xs">Start Date *</Label>
              <Input data-testid="event-date-start" type="date" value={form.date_start} onChange={e => updateForm('date_start', e.target.value)}
                className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" />
            </div>
            <div>
              <Label className="text-[#A1A1AA] text-xs">End Date</Label>
              <Input type="date" value={form.date_end} onChange={e => updateForm('date_end', e.target.value)}
                className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" />
            </div>
            <div>
              <Label className="text-[#A1A1AA] text-xs">Start Time</Label>
              <Input data-testid="event-time-start" type="time" value={form.time_start} onChange={e => updateForm('time_start', e.target.value)}
                className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" />
            </div>
            <div>
              <Label className="text-[#A1A1AA] text-xs">End Time</Label>
              <Input type="time" value={form.time_end} onChange={e => updateForm('time_end', e.target.value)}
                className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" />
            </div>

            {/* Location */}
            <div>
              <Label className="text-[#A1A1AA] text-xs">Venue Name</Label>
              <Input data-testid="event-location" value={form.location_name} onChange={e => updateForm('location_name', e.target.value)}
                className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="Conference Hall A" />
            </div>
            <div>
              <Label className="text-[#A1A1AA] text-xs">Address</Label>
              <Input value={form.location_address} onChange={e => updateForm('location_address', e.target.value)}
                className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="123 Main St, City" />
            </div>

            <div>
              <Label className="text-[#A1A1AA] text-xs">Organized By</Label>
              <Input data-testid="event-organized" value={form.organized_by} onChange={e => updateForm('organized_by', e.target.value)}
                className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="Your name or company" />
            </div>

            {/* Event Type */}
            <div>
              <Label className="text-[#A1A1AA] text-xs">Event Type</Label>
              <div className="mt-1 flex gap-1.5">
                {EVENT_TYPES.map(t => (
                  <button key={t.id} data-testid={`type-${t.id}`} onClick={() => updateForm('event_type', t.id)}
                    className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1 ${
                      form.event_type === t.id ? 'bg-violet-600 text-white' : 'bg-[#1E1E1E] text-[#A1A1AA] border border-[#27272A]'
                    }`}>
                    <t.icon className="w-3 h-3" /> {t.label}
                  </button>
                ))}
              </div>
            </div>

            {(form.event_type === 'online' || form.event_type === 'hybrid') && (
              <div className="sm:col-span-2">
                <Label className="text-[#A1A1AA] text-xs">Meeting Link</Label>
                <Input value={form.meeting_link} onChange={e => updateForm('meeting_link', e.target.value)}
                  className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="https://zoom.us/j/..." />
              </div>
            )}

            <div>
              <Label className="text-[#A1A1AA] text-xs">Max Capacity (leave empty for unlimited)</Label>
              <Input type="number" value={form.max_capacity} onChange={e => updateForm('max_capacity', e.target.value)}
                className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="100" />
            </div>
            <div>
              <Label className="text-[#A1A1AA] text-xs">Price (0 = Free)</Label>
              <Input data-testid="event-price" type="number" value={form.price} onChange={e => updateForm('price', e.target.value)}
                className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="0" />
            </div>
          </div>

          <Button data-testid="save-event-btn" onClick={handleSave} disabled={saving}
            className="w-full rounded-xl bg-violet-600 hover:bg-violet-700 h-10">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {editingId ? 'Update Event' : 'Create Event'}
          </Button>
        </motion.div>
      )}

      {/* Events List */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : events.length === 0 && !showForm ? (
        <div className="text-center py-20 bg-[#121212] rounded-2xl border border-[#27272A]">
          <Calendar className="w-12 h-12 text-[#525252] mx-auto mb-4" />
          <h3 className="text-white font-semibold text-lg mb-2">No events yet</h3>
          <p className="text-[#71717A] text-sm mb-4">Create your first event and invite people via your link</p>
          <Button onClick={() => setShowForm(true)} className="bg-violet-600 hover:bg-violet-700 rounded-xl">
            <Plus className="w-4 h-4 mr-1" /> Create Event
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map((evt, i) => {
            const status = getEventStatus(evt);
            const st = STATUS_STYLES[status];
            return (
              <motion.div key={evt.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className={`rounded-xl border p-4 transition-colors ${evt.is_active ? 'bg-[#121212] border-[#27272A]' : 'bg-[#0a0a0a] border-[#1a1a1a] opacity-60'}`}>
                <div className="flex gap-4">
                  {evt.poster_url && (
                    <img src={resolveMediaUrl(evt.poster_url)} alt="" className="w-24 h-24 rounded-xl object-cover flex-shrink-0 border border-[#27272A]" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="text-white font-semibold text-base">{evt.title}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${st.bg} ${st.text}`}>{st.label}</span>
                      {parseFloat(evt.price) > 0 ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/15 text-amber-400">${evt.price}</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-500/15 text-green-400">Free</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#71717A] flex-wrap">
                      {evt.date_start && (
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />
                          {new Date(evt.date_start + 'T00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          {evt.time_start && ` at ${evt.time_start}`}
                        </span>
                      )}
                      {evt.location_name && (
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {evt.location_name}</span>
                      )}
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {evt.rsvp_count || 0} RSVPs</span>
                    </div>
                    {evt.organized_by && <p className="text-[#525252] text-xs mt-1">By {evt.organized_by}</p>}
                  </div>
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    <button data-testid={`toggle-event-${evt.id}`} onClick={() => handleToggle(evt)} className="p-1.5 rounded-lg hover:bg-white/5" title={evt.is_active ? 'Hide' : 'Show'}>
                      {evt.is_active ? <Eye className="w-4 h-4 text-green-400" /> : <EyeOff className="w-4 h-4 text-[#525252]" />}
                    </button>
                    <button data-testid={`view-rsvps-${evt.id}`} onClick={() => viewRSVPs(evt)} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-violet-500/10 border border-violet-500/20 transition-colors" title="View RSVPs">
                      <Users className="w-4 h-4 text-violet-400" />
                      <span className="text-xs text-violet-400 font-medium">{evt.rsvp_count || 0} RSVPs</span>
                    </button>
                    <button onClick={() => handleEdit(evt)} className="p-1.5 rounded-lg hover:bg-white/5" title="Edit">
                      <Edit2 className="w-4 h-4 text-[#A1A1AA]" />
                    </button>
                    <button onClick={() => handleDelete(evt.id)} className="p-1.5 rounded-lg hover:bg-red-500/10" title="Delete">
                      <Trash2 className="w-4 h-4 text-red-400/60" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EventsManager;
